import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { Input } from '@shared/components/ui/shadcn/input';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeductPayment } from '../../hooks';
import { DeductPaymentRequest } from '../../models';

interface DeductPaymentButtonProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: number;
  fileStatus?: string;
}

export const DeductPaymentButton = ({ isOpen, onClose, fileId, fileStatus }: DeductPaymentButtonProps) => {
  const { t } = useTranslation('codCourierFileDetail');
  const [orderId, setOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const { deductPaymentAsync, isDeducting } = useDeductPayment(fileId);
  const { toast } = useToast();

  // Función helper para normalizar el estado
  const normalizeStatus = (status: string | undefined): string => {
    if (!status) return '';
    return status.toUpperCase();
  };

  const handleDeductPayment = async () => {
    // Validar que el archivo esté en estado imported
    const normalizedStatus = normalizeStatus(fileStatus);
    if (normalizedStatus !== 'IMPORTED') {
      toast({
        title: t('deductPayment.error.title', 'Error al descontar pago'),
        description: t(
          'deductPayment.error.file_not_imported',
          'Solo se pueden descontar pagos de archivos en estado importado',
        ),
        variant: 'destructive',
      });
      return;
    }

    if (!orderId) {
      toast({
        title: t('deductPayment.error.title', 'Error al descontar pago'),
        description: t('deductPayment.error.order_required', 'Debe ingresar el ID del pedido'),
        variant: 'destructive',
      });
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: t('deductPayment.error.title', 'Error al descontar pago'),
        description: t('deductPayment.error.amount_required', 'Debe ingresar un importe válido mayor a 0'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const data: DeductPaymentRequest = {
        order_id: parseInt(orderId, 10),
        amount: parseFloat(amount),
        ...(reason ? { reason } : {}),
      };

      await deductPaymentAsync(data);

      onClose();
      setOrderId('');
      setAmount('');
      setReason('');
    } catch {
      // El error ya se maneja en el hook
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOrderId('');
      setAmount('');
      setReason('');
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isDeducting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('deductPayment.modal_title', 'Descontar Pago del Archivo')}</DialogTitle>
          <DialogDescription>
            {t(
              'deductPayment.modal_description',
              'Registra una deducción o ajuste negativo en este archivo de courier.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* ID del Pedido */}
          <div className="space-y-2">
            <Label htmlFor="order_id">{t('deductPayment.order_id', 'ID del Pedido')} *</Label>
            <Input
              id="order_id"
              type="number"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder={t('deductPayment.order_id_placeholder', 'Ej: 12345')}
              disabled={isDeducting}
            />
          </div>

          {/* Importe a descontar */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('deductPayment.amount', 'Importe a Descontar')} *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('deductPayment.amount_placeholder', 'Ej: 20.00')}
              disabled={isDeducting}
            />
          </div>

          {/* Motivo */}
          <div className="space-y-2">
            <Label htmlFor="reason">
              {t('deductPayment.reason', 'Motivo de la Deducción')} ({t('deductPayment.optional', 'Opcional')})
            </Label>
            <TextArea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('deductPayment.reason_placeholder', 'Ej: Devolución por cliente insatisfecho')}
              disabled={isDeducting}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isDeducting}>
            {t('deductPayment.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleDeductPayment}
            disabled={
              isDeducting ||
              !orderId ||
              !amount ||
              parseFloat(amount) <= 0 ||
              normalizeStatus(fileStatus) !== 'IMPORTED'
            }
            variant="destructive"
          >
            {isDeducting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('deductPayment.deducting', 'Descontando...')}
              </>
            ) : (
              t('deductPayment.confirm', 'Descontar Pago')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
